// Logique pure de la fiche dépannage (portée telle quelle de DepFiche.js legacy).
// Aucune dépendance React/redux : tout est testable isolément.
import {
	DEPANNAGE_TYPE_APPEL_ASSISTANCE as APPEL_ASSURANCE,
	DEPANNAGE_TYPE_APPEL_POLICE as APPEL_POLICE,
	DEPANNAGE_TYPE_APPEL_PRIVE as APPEL_PRIVE,
	DEPANNAGE_ETAT_ANNULE,
	DEPANNAGE_ETAT_CLOS,
	HTtoTTC,
} from "../../utilities/Addons"

// Ids de destinations spéciales (référentiel Destination, codés en dur).
export const ID_FOUR = 3
export const ID_SUR_PLACE = 4
export const ID_SVP_MECA = 5

export const round2 = (x) => Math.round(x * 100) / 100

// Affichage d'un n° de dossier : depuis le 14.08.2026 (GE-37) les tirets sont
// CONSERVÉS en base et masqués à l'affichage (les valeurs legacy restent sans
// tirets). LECTURE SEULE UNIQUEMENT — ne jamais brancher sur un champ
// éditable : un input qui afficherait la valeur strippée la re-sauverait sans
// tirets et écraserait silencieusement la donnée en base.
export function formatNumDossier(value) {
	if (value === null || value === undefined) return ""
	return String(value).replace(/-/g, "")
}

// hSupTot = hSupQ × hSupT (0 si l'un des deux est vide/0). Les valeurs peuvent
// être des chaînes (inputs texte) : la multiplication coerce comme en legacy.
export function computeHSupTot(dep) {
	if (
		dep.hSupQ === 0 ||
		dep.hSupQ === "" ||
		dep.hSupT === 0 ||
		dep.hSupT === ""
	)
		return 0
	return round2(dep.hSupT * dep.hSupQ)
}

export function computeGardTot(dep) {
	if (
		dep.gardQ === 0 ||
		dep.gardQ === "" ||
		dep.gardT === 0 ||
		dep.gardT === ""
	)
		return 0
	return round2(dep.gardT * dep.gardQ)
}

// totalFinal = base + heures supp + gardiennage + restitution + Σ livraisons
// + Σ autres. GE-67 (Jordan 10.10, Q7) : plus de fournitures sur la fiche
// dépannage (elles sont facturées par la fiche atelier liée). Les livraisons
// ne sont plus éditables dans la V2 mais restent comptées.
export function computeTotalFinal(dep) {
	let tot = 0.0
	if (dep.baseTot !== "") tot += parseFloat(dep.baseTot)
	if (dep.hSupTot !== "") tot += parseFloat(dep.hSupTot)
	if (dep.gardTot !== "") tot += parseFloat(dep.gardTot)
	if (dep.restiTot !== "") tot += parseFloat(dep.restiTot)
	dep.livraisons.forEach((liv) => {
		if (liv.tot !== "") tot += parseFloat(liv.tot)
	})
	// GE-71 : montant signé ; saisie en cours (« - » seul) comptée 0.
	dep.autres.forEach((autre) => {
		const montant = parseFloat(autre.total)
		if (!isNaN(montant)) tot += montant
	})
	return round2(tot)
}

// Somme brute des transactions (non arrondie — le legacy compare === totalFinal).
export function computeTransTot(transactions) {
	let transTot = 0.0
	transactions.forEach((trans) => {
		if (trans.total !== "") transTot += parseFloat(trans.total)
	})
	return transTot
}

export function computeRestePayer(dep) {
	// Encaissements = TransactionV2 (saisie fiche + terrain re-lié) —
	// DepTransaction est mort.
	return round2(
		dep.totalFinal - round2(computeTransTot(dep.transactionsv2 || [])),
	)
}

// Validations avant sauvegarde. `mdpFacture` : objet moyen de paiement
// « Facture » (useMoyenPaiementFacture) ou undefined si pas encore chargé.
// Les règles livraison du legacy (cohérence livDate/livEmp/livHeure/livVeh,
// châssis si four/police + livraison) tombent avec l'UI livraisons (mobile).
// Types de point d'intérêt (miroir PointInteretComponent), pour la validation.
const POI_TYPE_ADRESSE = 4
const POI_TYPE_COORDONNEES = 6

// Validité d'un point d'intérêt (lieu/destination), par type — aligné sur
// DepMissionV2.isPoValid. Tolère null. Sans dépendance (constantes inline).
//  - Adresse : garage (label posé) → rue + localité suffisent ; sinon adresse
//    complète (rue, NPA, localité, pays — N° facultatif, revue 15.10 M1).
//  - Coordonnées : latitude + longitude (dans adresse.extra_data).
//  - Parc / Fourrière : POI référentiel sélectionné (id non null).
function isPoValid(po) {
	if (!po) return false
	const a = po.adresse || {}
	if (po.type === POI_TYPE_ADRESSE) {
		if (po.label) return !!(a.rue && a.localite)
		return !!(a.rue && a.code_postal && a.localite && a.pays)
	}
	if (po.type === POI_TYPE_COORDONNEES) {
		const extra = a.extra_data || {}
		return !!(extra.latitude && extra.longitude)
	}
	return po.id !== null && po.id !== undefined
}

// `destinationInconnue` : pastille « Inconnue » de la carte Destination —
// la fiche se sauve avec destination_finale = null, pas d'erreur bloquante.
export function checkErrors(dep, mdpFacture, destinationInconnue = false) {
	const {
		heure,
		date,
		appel_type,
		depanneur,
		lieu_intervention,
		destination_finale,
		vehicule_depannage,
		avec_cle,
		accident_ou_enlevement,
		secteur_police,
		vehicule_categorie,
		assistance_entite,
	} = dep
	const errors = []
	const mdpFactureId = mdpFacture ? mdpFacture.id : 0
	// Commun à tous les types.
	if (date.includes(" ") || date.length !== 10) errors.push("Date")
	if (heure.includes(" ") || heure.length !== 5) errors.push("Heure")
	if (![APPEL_ASSURANCE, APPEL_POLICE, APPEL_PRIVE].includes(appel_type))
		errors.push("Type d'appel")
	if (depanneur === 0) errors.push("Dépanneur")
	// Fiche issue d'une mission terrain (GE-47) : lieu parfois inconnu (pas
	// de GPS) → avertissement, pas un blocage (revue 02.10, R10).
	if (!isPoValid(lieu_intervention) && !dep.mission_terrain)
		errors.push("Lieu")
	// Dépanné sur place : pas de transport, la destination n'existe pas.
	// Déclarée inconnue : acceptée telle quelle (NULL en base).
	if (!dep.surPlace && !destinationInconnue && !isPoValid(destination_finale))
		errors.push("Destination")
	if (vehicule_depannage === 0) errors.push("Dépanneuse")
	// Assistance : donneur d'ordre (le n° dossier est différable, GE-46).
	if (appel_type === APPEL_ASSURANCE) {
		if (assistance_entite === 0) errors.push("Assistance")
	}
	// Police : clé, motif, secteur, catégorie (matricule différable, GE-46).
	if (appel_type === APPEL_POLICE) {
		if (avec_cle === 0) errors.push("Avec clé ?")
		if (accident_ou_enlevement === 0) errors.push("Accident / enlèvement")
		if (secteur_police === 0) errors.push("Lieu d'intervention police")
		if (vehicule_categorie === 0) errors.push("Catégorie véhicule")
	}
	// GE-71 : lignes Autres signées (rabais) — montant lisible, et le total
	// final ne passe jamais sous 0.
	const autres = dep.autres || []
	autres.forEach((a, i) => {
		const brut =
			a.total === undefined || a.total === null ? "" : `${a.total}`
		if (brut.trim() === "" || isNaN(Number(brut)))
			errors.push(`Autres ligne ${i + 1} : montant`)
	})
	// totalFinal = computeTotalFinal, posé par useDepFiche à chaque saisie.
	if (parseFloat(dep.totalFinal) < 0)
		errors.push("Le rabais dépasse le total (total final négatif)")
	// Transactions de la fiche (V2) ; les encaissements terrain (dep_mission
	// posé) sont verrouillés et complets — jamais bloquants. Numéro = rang
	// dans la carte, terrain compris (retour client 15.10, police : la ligne
	// ajoutée suit l'encaissement terrain).
	const transactions = dep.transactionsv2 || []
	transactions.forEach((t, i) => {
		if (
			!t.dep_mission &&
			parseFloat(t.total) !== 0 &&
			((t.caissier === 0 && mdpFactureId !== t.moyenPaie) ||
				t.moyenPaie === 0)
		) {
			let str = ""
			if (t.moyenPaie === 0) {
				if (str === "") str += " moyen de paiement"
				else str += ", moyen de paiement"
			}
			if (t.caissier === 0 && mdpFactureId !== t.moyenPaie) {
				if (str === "") str += " caissier"
				else str += ", caissier"
			}
			errors.push(`Transactions ligne ${i + 1}:${str}`)
		}
	})
	return errors
}

// GE-46 : champs DIFFÉRABLES encore vides — avertissement à la sauvegarde
// (permise), la fiche reste « incomplète » et le passage en « Contrôle avant
// facturation » est refusé par le serveur tant qu'ils manquent. Miroir de
// champs_manquants (backend/svp/depannage_completude.py : B9, clés terrain
// GE-47, D8) ; le poste de police, le châssis fourrière et l'e-mail ne se
// lisent qu'au serveur (bandeau).
const POI_TYPE_FOURRIERE = 1
const POI_TYPE_A_VIDE = 2
const POI_TYPE_ANNULE = 5
const vide = (v) => (v || "").trim() === ""

export function checkAvertissements(dep) {
	// Miroir de ETATS_JAMAIS_INCOMPLETS (revue 07.10, R9). La date de mise en
	// service (DEPANNAGE_DEPUIS) n'est connue que du serveur : non reflétée.
	if ([DEPANNAGE_ETAT_CLOS, DEPANNAGE_ETAT_ANNULE].includes(dep.etat))
		return []
	const avertissements = []
	const dest = dep.destination_finale
	const destType =
		!!dest && dest.id !== null && dest.id !== undefined ? dest.type : null
	const police = dep.appel_type === APPEL_POLICE
	// GE-58 (point 5) : en police, la course à vide décrit quand même le
	// véhicule — seul « Annulé » en dispense.
	const sansVehicule = police
		? destType === POI_TYPE_ANNULE
		: [POI_TYPE_A_VIDE, POI_TYPE_ANNULE].includes(destType)
	// Miroir de cles_manquantes (svp/depannage_completude.py) — véhicule
	// pour tout appel (sauf sans véhicule), identité du client hors police.
	if (!sansVehicule) {
		// GE-58 (point 4) : châssis seulement en fourrière / séquestre
		// (diagnostique_police 3 / 4 ; fiche bureau : destination fourrière).
		const diag = dep.diagnostique_info
			? dep.diagnostique_info.diagnostique_police
			: null
		const chassisExige = dep.diagnostique_info
			? [3, 4].includes(diag)
			: destType === POI_TYPE_FOURRIERE
		if (police && chassisExige && vide(dep.chassis))
			avertissements.push("Châssis")
		if (vide(dep.marque)) avertissements.push("Marque")
		if (vide(dep.modele)) avertissements.push("Modèle")
		if (vide(dep.plaques)) avertissements.push("Plaques")
	}
	// GE-58 (point 5) : course à vide police encaissée par le client.
	const encaisseeClient = (dep.transactionsv2 || []).some(
		(t) => t.dep_mission && t.payeParSelect === 1,
	)
	if (police && destType === POI_TYPE_A_VIDE && encaisseeClient) {
		if (vide(dep.nom)) avertissements.push("Nom du client")
		if (vide(dep.prenom)) avertissements.push("Prénom du client")
		if (vide(dep.adresse_client_0)) avertissements.push("Adresse du client")
	}
	if (dep.appel_type === APPEL_ASSURANCE && vide(dep.num_dossier))
		avertissements.push("N° dossier")
	if (dep.appel_type !== APPEL_POLICE) {
		if (vide(dep.nom)) avertissements.push("Nom du client")
		if (vide(dep.prenom)) avertissements.push("Prénom du client")
		if (vide(dep.telephone)) avertissements.push("Téléphone du client")
	}
	// GE-52 : pas d'agent sur place → pas de matricule attendu.
	if (
		dep.appel_type === APPEL_POLICE &&
		dep.agent_sur_place !== false &&
		vide(dep.matricule)
	)
		avertissements.push("Matricule")
	if (dep.mission_terrain && !isPoValid(dep.lieu_intervention))
		avertissements.push("Lieu d'intervention")
	return avertissements
}

// Port de checkShow() : quelles sous-sections ouvrir au chargement selon la
// présence de données. Pilote les `defaultOpen` des cartes repliables.
export function sectionStatus(dep) {
	const showSinistre =
		dep.numSinistre !== "" ||
		dep.franchise !== 0 ||
		dep.rabaisFranchise !== 0 ||
		dep.couvLocation !== ""
	const showFautif =
		dep.fautifNom !== "" ||
		dep.fautifVoiture !== "" ||
		dep.fautifPlaques !== "" ||
		dep.fautifAssurance !== ""
	const showExpert =
		dep.expertDate !== "" ||
		dep.expertDateEffective !== "" ||
		dep.expertNom !== ""
	const showCleSupp = dep.cleSupp !== 0 || dep.infoCleSupp !== ""
	const showPlaques =
		dep.carteGrise ||
		dep.carteGriseDate !== "" ||
		dep.infoCarteGrise !== "" ||
		dep.avecPlaques ||
		dep.plaquesReprisLe !== "" ||
		dep.infoAvecPlaques !== ""
	const showAutre = dep.effetsPers !== "" || dep.polAutre !== ""
	return {
		sinistre:
			showSinistre ||
			showCleSupp ||
			showFautif ||
			showExpert ||
			showPlaques ||
			showAutre,
		showSinistre,
		showFautif,
		showCleSupp,
		showExpert,
		showPlaques,
		showAutre,
		showInfoSup: dep.hSupInfo !== "",
		showInfoGard: dep.gardInfo !== "",
	}
}
